<template>
  <div class="modal offer-modal skin-modal top-up-modal">
    <div class="overlay" @click="close"></div>
    <div class="wrapper">
      <img class="close" @click="close" src="./../img/cross.svg" />
      <div class="container">
        <div class="form">
          <div class="form-wrapper">
            <div class="form-fields">
              <div class="title title-24">
                <b>Top up balance</b>
              </div>
              <div class="form-fields-wrapper">
                <div class="balance-rate desc">
                  1 {{ currencyName }} = {{ selectedCurrencyRate }}
                  {{ currencySymbol }}
                </div>
                <div class="input-container">
                  <div class="desc">Amount ({{ currencyName }}):</div>
                  <input
                    type="text"
                    placeholder="Amount"
                    v-model="amount"
                    @input="allowOnlyMoney"
                  />
                </div>
                <div class="payment-preview desc" v-if="topUpAmountIsValid">
                  To pay: {{ topUpPaymentAmount }} {{ currencySymbol }}
                </div>
                <div class="input-container">
                  <div class="desc">First name:</div>
                  <input type="text" placeholder="First name" v-model="name" />
                </div>
                <div class="input-container">
                  <div class="desc">Last name:</div>
                  <input
                    type="text"
                    placeholder="Last name"
                    v-model="surname"
                  />
                </div>
                <div class="input-container">
                  <div class="desc">Phone:</div>
                  <input type="tel" placeholder="Phone" v-model="phone" />
                </div>
                <div class="input-container">
                  <div class="desc">Country:</div>
                  <Select2
                    class="select2 single"
                    v-model="country"
                    :options="countryOptions"
                    @select="selectCountry($event)"
                    placeholder="Country"
                  />
                </div>
                <div class="input-container">
                  <div class="desc">City:</div>
                  <input type="text" placeholder="City" v-model="city" />
                </div>
                <div class="input-container">
                  <div class="desc">Address:</div>
                  <input type="text" placeholder="Address" v-model="address" />
                </div>
                <div class="input-container">
                  <div class="desc">Post Code:</div>
                  <input
                    type="text"
                    placeholder="Post Code"
                    v-model="postCode"
                  />
                </div>
                <div
                  class="button-list top-up-methods"
                  v-if="availablePaymentMethods.length"
                >
                  <div
                    class="img-btn-container"
                    v-for="(method, i) in availablePaymentMethods"
                    :key="i"
                  >
                    <button
                      :class="[
                        'img-btn',
                        { disabled: !fieldsAreFilled || isLoading },
                      ]"
                      :disabled="!fieldsAreFilled || isLoading"
                      @click="submit(method.code)"
                    >
                      <img
                        v-if="method.image"
                        :src="imageUrl(method.image)"
                        :alt="method.title || method.name || method.code"
                        class="img"
                      />
                      <span v-else>{{
                        method.title || method.name || method.code
                      }}</span>
                    </button>
                    <a
                      v-if="shouldShowBlikRegulations(method)"
                      href="https://www.igoriacard.com/regulamin-i-oplaty/regulamin-BLIK/"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="blik-regulations-link"
                    >
                      BLIK Regulations
                    </a>
                  </div>
                </div>
                <transition name="fade">
                  <div class="error-desc desc red" v-if="error">
                    {{ error }}
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Select2 from "vue3-select2-component";
import { mapGetters } from "vuex";

export default {
  name: "TopUpModal",
  components: {
    Select2,
  },
  props: ["initialAmount", "profile"],
  data() {
    return {
      amount: "",
      name: "",
      surname: "",
      phone: "",
      country: "",
      countryId: "",
      city: "",
      address: "",
      postCode: "",
      countryOptions: [],
      countryOptionsId: [],
      error: "",
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("app", [
      "currency",
      "availablePaymentMethods",
      "internalCurrency",
      "internalCurrencyEnabled",
      "currencyRate",
      "settings",
      "currencySymbol",
    ]),
    fieldsAreFilled() {
      return (
        this.topUpAmountIsValid &&
        this.name &&
        this.surname &&
        this.phone &&
        this.countryId &&
        this.city &&
        this.address &&
        this.postCode
      );
    },
    topUpAmountIsValid() {
      return parseFloat(this.amount) > 0;
    },
    internalCurrencyEurRate() {
      return this.internalCurrency && this.internalCurrency.exchange_rate_eur
        ? this.internalCurrency.exchange_rate_eur
        : 1;
    },
    selectedCurrencyRate() {
      const rate = this.internalCurrencyEurRate * this.currencyRate;
      return this.formatRate(rate);
    },
    topUpPaymentAmount() {
      const amount = parseFloat(this.amount);
      if (Number.isNaN(amount)) return "0.00";

      if (!this.internalCurrencyEnabled) return this.formatMoney(amount);

      return this.formatMoney(
        amount * this.internalCurrencyEurRate * this.currencyRate,
      );
    },
    currencyName() {
      if (!this.internalCurrencyEnabled) return this.currency;
      return (
        this.internalCurrency.name || this.internalCurrency.symbol || "coin"
      );
    },
    formatRate() {
      return (value) => {
        const numericValue = parseFloat(value);
        if (Number.isNaN(numericValue)) return value;

        return numericValue.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        });
      };
    },
    formatMoney() {
      return (value) => {
        const numericValue = parseFloat(value);
        if (Number.isNaN(numericValue)) return "0.00";

        return numericValue.toFixed(2);
      };
    },
  },
  mounted() {
    this.amount = this.initialAmount || "";
    this.fillFromProfile(true);
    this.getCountries();
  },
  watch: {
    profile: {
      handler() {
        this.fillFromProfile(false);
      },
      deep: true,
    },
  },
  methods: {
    fillFromProfile(overwrite) {
      const profile = this.profile || {};
      this.name = overwrite || !this.name ? profile.name || "" : this.name;
      this.surname =
        overwrite || !this.surname ? profile.surname || "" : this.surname;
      this.phone = overwrite || !this.phone ? profile.phone || "" : this.phone;
      this.city = overwrite || !this.city ? profile.city || "" : this.city;
      this.address =
        overwrite || !this.address ? profile.address || "" : this.address;
      this.postCode =
        overwrite || !this.postCode ? profile.zip || "" : this.postCode;
      this.countryId =
        overwrite || !this.countryId ? profile.country || "" : this.countryId;
    },
    imageUrl(url) {
      if (!url) return "";
      if (/^https?:\/\//.test(url)) return url;
      return this.$parent.appDomain + url;
    },
    isBlikMethod(method) {
      return method && method.code && method.code.toLowerCase() === "blik";
    },
    shouldShowBlikRegulations(method) {
      return (
        this.isBlikMethod(method) &&
        this.currency === "PLN" &&
        this.settings &&
        this.settings.visitor_country === "PL"
      );
    },
    allowOnlyMoney() {
      this.amount = this.amount.replace(/[^0-9.,]/g, "");
      const parts = this.amount.split(/[.,]/);
      const integerPart = parts[0];
      let decimalPart = parts[1];
      if (decimalPart !== undefined && decimalPart.length > 2) {
        decimalPart = decimalPart.slice(0, 2);
      }
      this.amount =
        decimalPart !== undefined
          ? `${integerPart}.${decimalPart}`
          : integerPart;
    },
    selectCountry(event) {
      this.country = event.text;
      for (let i = 0; i < this.countryOptions.length; i++) {
        if (this.countryOptions[i] == event.text) {
          this.countryId = this.countryOptionsId[i];
        }
      }
    },
    getCountries() {
      this.$http
        .get(process.env.VUE_APP_API + "countries")
        .then((res) => {
          const countries = res.data.payload || [];
          this.countryOptions = countries.map((item) => item.title);
          this.countryOptionsId = countries.map((item) => item.id);
          if (this.countryId) {
            const selectedIndex = this.countryOptionsId.findIndex(
              (id) => String(id) === String(this.countryId),
            );
            if (selectedIndex !== -1)
              this.country = this.countryOptions[selectedIndex];
          }
        })
        .catch((res) => {
          if (res.response && res.response.status === 401) {
            this.$parent.openSignIn();
          }
        });
    },
    submit(paymentType) {
      if (!this.fieldsAreFilled || this.isLoading) return;

      this.isLoading = true;
      this.error = "";

      const data = {
        amount: this.internalCurrencyEnabled
          ? this.topUpPaymentAmount
          : this.amount,
        name: this.name,
        surname: this.surname,
        phone: this.phone,
        country: this.countryId,
        city: this.city,
        address: this.address,
        postCode: this.postCode,
        paymentType,
        currency: this.currency,
        saveData: true,
      };

      this.$http
        .post(process.env.VUE_APP_API + "deposit/checkout", data)
        .then((res) => {
          if (res.data.status === "OK" && res.data.redirect_url) {
            window.location.href = res.data.redirect_url;
            return;
          }
          this.error = res.data.message || "Could not start top up.";
        })
        .catch((res) => {
          if (res.response && res.response.status === 401) {
            this.$parent.openSignIn();
            return;
          }
          this.error =
            (res.response && res.response.data && res.response.data.message) ||
            "Could not start top up.";
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.top-up-modal {
  z-index: 1060;
}

.top-up-modal .wrapper {
  max-width: 640px;
}

.top-up-modal ::v-deep .select2-container,
.top-up-modal ::v-deep .select2 {
  width: 100% !important;
  max-width: 100%;
}

.top-up-modal ::v-deep .select2-container {
  margin-top: 12px;
  position: relative;
  z-index: 1052;
}

.top-up-modal ::v-deep .select2-container--default .select2-selection--single {
  width: 100%;
  height: 51px;
  background: #f7f7f7;
  border: 1px solid #bebebe;
  border-radius: 8px !important;
}

.top-up-modal
  ::v-deep
  .select2-container--default
  .select2-selection--single
  .select2-selection__rendered {
  height: 49px;
  padding: 15px 25px;
  border: 0 !important;
  color: #000;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;
}

.top-up-modal
  ::v-deep
  .select2-container--default
  .select2-selection--single
  .select2-selection__placeholder {
  color: #909090;
}

.top-up-modal
  ::v-deep
  .select2-container--default
  .select2-selection--single
  .select2-selection__arrow {
  top: 12px;
  right: 16px;
}

.top-up-methods {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-top: 18px;
  width: 100%;
}

.payment-preview {
  color: #96a4c4;
  margin-top: -4px;
}

.img-btn-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.img-btn {
  width: 100%;
  min-height: 56px;
  background: #f7f7f7;
  color: #363d47;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 18px;
  line-height: 24px;
  padding: 10px 16px;
  border-radius: 8px;
  transition: 0.3s;
}

.img-btn:hover {
  background: #ececec;
}

.img-btn .img {
  max-width: 120px;
  max-height: 34px;
  width: auto;
  object-fit: contain;
}

.img-btn.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.blik-regulations-link {
  color: #5277d1;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  text-decoration: none;
}

.blik-regulations-link:hover {
  text-decoration: underline;
}
</style>
